import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { AlertTriangle, Droplets } from "lucide-react";
import { getSharedDampScan } from "@/lib/damp-scan.functions";
import type { DampResult } from "@/components/DampAiScanner";

const BASE = "https://www.akhusbilfritid.se";

export const Route = createFileRoute("/fuktrapport/$token")({
  loader: async ({ params }) => {
    const { scan, photoUrls } = await getSharedDampScan({ data: { token: params.token } });
    if (!scan) throw notFound();
    return { scan, photoUrls };
  },
  head: ({ loaderData }) => {
    const area = loaderData?.scan?.area || "husbil eller husvagn";
    const vehicle = loaderData?.scan?.vehicle ? ` – ${loaderData.scan.vehicle}` : "";
    const title = `Fuktrapport: ${area}${vehicle}`.slice(0, 60);
    const description = `Delad fuktrapport från AK Husbil & Fritid: riskbedömning steg ${
      loaderData?.scan?.risk_level ?? 0
    } av 4 för ${area}. Bilder, fynd, mätpunkter och åtgärder.`.slice(0, 158);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, follow" },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  errorComponent: () => <Fallback text="Rapporten kunde inte visas just nu." />,
  notFoundComponent: () => (
    <Fallback text="Den här rapporten finns inte, eller så har ägaren slutat dela den." />
  ),
  component: SharedDampReport,
});

function Fallback({ text }: { text: string }) {
  return (
    <section className="container-edge py-20">
      <h1 className="font-display text-2xl">Fuktrapport</h1>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">{text}</p>
      <Link
        to="/fukttest-husbil"
        className="mt-6 inline-flex rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
      >
        Gör en egen fuktskanning
      </Link>
    </section>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3 rounded-2xl border border-border p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </div>
  );
}

function SharedDampReport() {
  const { scan, photoUrls } = Route.useLoaderData();
  const a = (scan.assessment ?? {}) as Partial<DampResult>;
  const level = Math.min(4, Math.max(0, scan.risk_level));

  return (
    <section className="container-edge py-14 md:py-20">
      <p className="eyebrow">Delad rapport · Fuktskannern</p>
      <h1 className="mt-2 flex items-center gap-2 font-display text-2xl md:text-3xl">
        <Droplets className="h-6 w-6 text-accent" aria-hidden="true" />
        {scan.area || "Fuktrapport"}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {new Date(scan.created_at).toLocaleDateString("sv-SE", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
        {scan.vehicle ? ` · ${scan.vehicle}` : ""}
      </p>

      <div className="mt-6 max-w-3xl rounded-3xl border border-border bg-card p-5 sm:p-7">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Riskbedömning
          </span>
          <span className="font-display text-lg">{scan.risk_label || `Steg ${level}`}</span>
        </div>
        <div className="mt-2 flex gap-1" aria-hidden="true">
          {[1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className={`h-2 flex-1 rounded-full ${
                n <= level ? (level >= 4 ? "bg-destructive" : "bg-accent") : "bg-border"
              }`}
            />
          ))}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">Steg {level} av 4</p>

        {a.summary && <p className="mt-4 text-sm leading-relaxed">{a.summary}</p>}

        {photoUrls.length > 0 && (
          <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {photoUrls.map((url, i) => (
              <li key={url} className="overflow-hidden rounded-xl border border-border">
                <img
                  src={url}
                  alt={`Fuktrapportens bild ${i + 1} av ${scan.area || "området"}`}
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
              </li>
            ))}
          </ul>
        )}

        {scan.estimated_moisture_pct && (
          <Block title="Uppskattat fuktvärde">{scan.estimated_moisture_pct}</Block>
        )}
        {scan.meter_value && <Block title="Fuktmätaren visade">{scan.meter_value}</Block>}
        {scan.note && <Block title="Ägarens egna ord">{scan.note}</Block>}
        {a.woodCondition && <Block title="Träet och plywooden">{a.woodCondition}</Block>}
        {a.likelySource && <Block title="Varifrån vattnet troligen kommer">{a.likelySource}</Block>}

        {a.findings && a.findings.length > 0 && (
          <Block title="Vad analysen ser">
            <ul className="space-y-2">
              {a.findings.map((f) => (
                <li key={f.what}>
                  <span className="font-medium text-foreground">{f.what}</span>
                  {f.where ? ` · ${f.where}` : ""}
                  {f.meaning ? <span className="block">{f.meaning}</span> : null}
                </li>
              ))}
            </ul>
          </Block>
        )}

        {a.measurePoints && a.measurePoints.length > 0 && (
          <Block title="Mät här med stiftfuktmätare">
            <ul className="list-disc space-y-1 pl-5">
              {a.measurePoints.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Block>
        )}

        {a.actions && a.actions.length > 0 && (
          <Block title="Åtgärder i prioritetsordning">
            <ol className="list-decimal space-y-1 pl-5">
              {a.actions.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ol>
            {a.costEstimate ? <p className="mt-2">Kostnad: {a.costEstimate}</p> : null}
          </Block>
        )}

        <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
          {a.disclaimer ||
            "Bedömningen bygger bara på bilderna. Mät alltid med stiftfuktmätare innan du bestämmer åtgärd."}
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/fukttest-husbil"
          className="inline-flex rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
        >
          Gör en egen fuktskanning
        </Link>
        <Link
          to="/boka-service"
          className="inline-flex rounded-full border border-border px-5 py-2.5 text-sm font-medium"
        >
          Boka fuktmätning i verkstad
        </Link>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Rapporten är delad av ägaren via en hemlig länk. Se hela guiden på {BASE}/fukttest-husbil.
      </p>
    </section>
  );
}
